import { useState, useEffect, useRef } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X, Globe, ChevronDown, Mail, User } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { useLanguage } from "@/lib/i18n";

const NAV_LINKS = [
  { href: "/", key: "home" },
  { href: "/services", key: "services" },
  { href: "/events", key: "events" },
  { href: "/tickets", key: "tickets", literal: "Tickets" },
  { href: "/portfolio", key: "portfolio" },
  { href: "/studio", key: "studio", literal: "Studio", isNew: true },
  { href: "/marketplace", key: "marketplace" },
  { href: "/shop", key: "shop" },
  { href: "/cartblancja", key: "tech" },
  { href: "/chauffeur", key: "chauffeur", literal: "Chauffeur" },
  { href: "/booking", key: "bookNow" },
];

// Read auth_token reactively so the CTA disappears the moment the user
// signs in / out, without forcing every consumer to wire up a context.
function useAuthToken() {
  const [tok, setTok] = useState(() => {
    if (typeof window === "undefined") return null;
    return localStorage.getItem("auth_token");
  });
  useEffect(() => {
    const sync = () => setTok(localStorage.getItem("auth_token"));
    window.addEventListener("storage", sync);
    // Same-window changes don't fire 'storage' — poll lightly so the CTA
    // updates after a successful login on the same tab.
    const id = setInterval(sync, 1500);
    return () => { window.removeEventListener("storage", sync); clearInterval(id); };
  }, []);
  return tok;
}

export default function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [langOpen, setLangOpen] = useState(false);
  const location = useLocation();
  const { lang, switchLang, t, LANG_LABELS } = useLanguage();
  const langRef = useRef(null);
  const authToken = useAuthToken();
  const isSignedIn = !!authToken && authToken !== "pending";

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => { setMobileOpen(false); }, [location]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (langRef.current && !langRef.current.contains(e.target)) setLangOpen(false);
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  return (
    <header
      data-testid="main-header"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        scrolled ? "bg-black/80 backdrop-blur-xl border-b border-white/5" : "bg-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12 flex items-center justify-between h-20">
        <Link to="/" className="flex items-center gap-3" data-testid="logo-link">
          <span className="font-heading text-2xl md:text-3xl font-light tracking-tight text-white">
            Aristocrats<span className="text-gold ml-1">JA</span>
          </span>
        </Link>

        {/* Desktop Nav */}
        <nav className="hidden xl:flex items-center gap-7" data-testid="desktop-nav">
          {NAV_LINKS.map((link) => (
            <Link
              key={link.href}
              to={link.href}
              data-testid={`nav-${link.key}`}
              className={`relative line-reveal text-sm font-body font-light tracking-wide transition-colors pb-1 ${
                location.pathname === link.href
                  ? "text-gold"
                  : "text-white/70 hover:text-white"
              }`}
            >
              {link.literal || t(`nav.${link.key}`)}
              {link.isNew && (
                <span
                  data-testid={`nav-${link.key}-new-badge`}
                  className="absolute -top-2 -right-5 text-[8px] font-body font-medium uppercase tracking-wider text-luxury-black bg-gold rounded-sm px-1 py-0.5 leading-none"
                >
                  New
                </span>
              )}
            </Link>
          ))}
          {/* Auth CTA — only shown to signed-out visitors so they can find the email signup from anywhere. */}
          {!isSignedIn && (
            <Link
              data-testid="header-signup-cta"
              to="/vip?tab=register"
              className="inline-flex items-center gap-1.5 text-xs font-body uppercase tracking-wider text-gold border border-gold/40 hover:bg-gold/10 hover:border-gold rounded-md px-3 py-1.5 transition-colors"
            >
              <Mail size={12} /> Sign up with email
            </Link>
          )}
          {isSignedIn && (
            <Link
              data-testid="header-account-link"
              to="/vip/dashboard"
              className="inline-flex items-center gap-1.5 text-xs font-body uppercase tracking-wider text-gold/80 hover:text-gold transition-colors"
            >
              <User size={12} /> Account
            </Link>
          )}
          {/* Language Dropdown */}
          <div className="relative" ref={langRef}>
            <button data-testid="lang-toggle" onClick={() => setLangOpen(!langOpen)} className="flex items-center gap-1.5 text-white/40 hover:text-gold transition-colors text-xs font-body">
              <Globe size={14} /> {LANG_LABELS[lang]} <ChevronDown size={10} className={`transition-transform ${langOpen ? "rotate-180" : ""}`} />
            </button>
            <AnimatePresence>
              {langOpen && (
                <motion.div
                  initial={{ opacity: 0, y: -4 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -4 }}
                  transition={{ duration: 0.15 }}
                  className="absolute top-8 right-0 border border-white/10 bg-luxury-black/95 backdrop-blur-xl z-50 py-1 min-w-[140px] max-h-80 overflow-y-auto"
                >
                  {Object.entries(LANG_LABELS).map(([code, label]) => (
                    <button key={code} data-testid={`lang-${code}`} onClick={() => { switchLang(code); setLangOpen(false); }} className={`block w-full text-left px-4 py-2.5 text-xs font-body transition-colors ${lang === code ? "text-gold bg-gold/10" : "text-white/60 hover:text-white hover:bg-white/5"}`}>
                      {label}
                    </button>
                  ))}
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </nav>

        {/* Mobile Toggle */}
        <button
          data-testid="mobile-menu-toggle"
          className="xl:hidden text-white/80 hover:text-gold transition-colors"
          onClick={() => setMobileOpen(!mobileOpen)}
        >
          {mobileOpen ? <X size={24} /> : <Menu size={24} />}
        </button>
      </div>

      {/* Mobile Nav */}
      <AnimatePresence>
        {mobileOpen && (
          <motion.nav
            data-testid="mobile-nav"
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            className="xl:hidden bg-black/95 backdrop-blur-xl border-b border-white/5 overflow-hidden"
          >
            <div className="px-6 py-6 flex flex-col gap-4">
              {NAV_LINKS.map((link) => (
                <Link
                  key={link.href}
                  to={link.href}
                  data-testid={`mobile-nav-${link.key}`}
                  className={`flex items-center gap-2 text-base font-body font-light tracking-wide transition-colors ${
                    location.pathname === link.href ? "text-gold" : "text-white/70"
                  }`}
                >
                  {link.literal || t(`nav.${link.key}`)}
                  {link.isNew && (
                    <span className="text-[8px] font-body font-medium uppercase tracking-wider text-luxury-black bg-gold rounded-sm px-1 py-0.5 leading-none">
                      New
                    </span>
                  )}
                </Link>
              ))}
              {!isSignedIn && (
                <Link
                  data-testid="mobile-signup-cta"
                  to="/vip?tab=register"
                  className="inline-flex items-center gap-1.5 text-base font-body uppercase tracking-wider text-gold border border-gold/40 hover:bg-gold/10 rounded-md px-4 py-2 transition-colors mt-2"
                >
                  <Mail size={14} /> Sign up with email
                </Link>
              )}
              <div className="flex flex-wrap gap-2 pt-3 border-t border-white/5">
                {Object.entries(LANG_LABELS).map(([code, label]) => (
                  <button key={code} onClick={() => { switchLang(code); setMobileOpen(false); }} className={`text-xs font-body px-3 py-1.5 transition-colors border ${lang === code ? "text-gold bg-gold/10 border-gold/30" : "text-white/40 border-white/10 hover:border-white/20"}`}>{label}</button>
                ))}
              </div>
            </div>
          </motion.nav>
        )}
      </AnimatePresence>
    </header>
  );
}
